import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface Breadcrumb {
  label: string;
  url: string;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private router = inject(Router);

  private breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  private dynamicLabels = new Map<string, string>();
  private stateOverride: Breadcrumb[] | null = null;

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        // Clear stale dynamic labels from previous page
        this.dynamicLabels.clear();

        // Check for breadcrumb override passed via navigation state
        const nav = this.router.getCurrentNavigation();
        const override = nav?.extras?.state?.['breadcrumbOverride'] as
          | Breadcrumb[]
          | undefined;
        if (override) {
          this.stateOverride = override;
        } else if (nav) {
          // New navigation without override — clear it
          this.stateOverride = null;
        }
        // If stateOverride exists (from this or a previous set), also check history.state
        // to survive re-emits from setDynamicLabel
        if (!this.stateOverride && window.history.state?.breadcrumbOverride) {
          this.stateOverride = window.history.state.breadcrumbOverride;
        }

        this.emitBreadcrumbs();
      });
  }

  getBreadcrumbs(): Observable<Breadcrumb[]> {
    return this.breadcrumbs$.asObservable();
  }

  getCurrentBreadcrumbs(): Breadcrumb[] {
    const crumbs = this.breadcrumbs$.getValue().map((c) => ({ ...c }));
    if (crumbs.length > 0 && !crumbs[crumbs.length - 1].url) {
      crumbs[crumbs.length - 1].url = this.router.url;
    }
    return crumbs;
  }

  /**
   * Set a dynamic label for a route parameter placeholder.
   * For example, setDynamicLabel(':employeeId', 'John Smith')
   * will replace the ':employeeId' breadcrumb label with 'John Smith'.
   */
  setDynamicLabel(key: string, label: string): void {
    this.dynamicLabels.set(key, label);
    // Re-emit breadcrumbs with the updated label
    this.emitBreadcrumbs();
  }

  clearDynamicLabel(key: string): void {
    this.dynamicLabels.delete(key);
  }

  private emitBreadcrumbs(): void {
    if (this.stateOverride) {
      // Resolve any dynamic placeholders in the override
      const resolved = this.stateOverride.map((crumb) => ({
        label: this.resolveLabel(crumb.label),
        url: crumb.url,
      }));
      this.breadcrumbs$.next(resolved);
    } else {
      const root = this.router.routerState.snapshot.root;
      const crumbs = this.buildBreadcrumbs(root);
      this.breadcrumbs$.next(crumbs);
    }
  }

  private resolveLabel(label: string): string {
    if (label.startsWith(':')) {
      return this.dynamicLabels.get(label) || label;
    }
    return label;
  }

  private buildBreadcrumbs(
    route: ActivatedRouteSnapshot,
    url: string = '',
    crumbs: Breadcrumb[] = [],
  ): Breadcrumb[] {
    const children = route.children;

    for (const child of children) {
      const routeUrl = child.url.map((segment) => segment.path).join('/');
      const nextUrl = routeUrl ? `${url}/${routeUrl}` : url;

      // Check for breadcrumbPath (explicit parent chain for flat detail routes)
      if (child.data && child.data['breadcrumbPath']) {
        const path = child.data['breadcrumbPath'] as {
          label: string;
          url: string;
        }[];
        for (const entry of path) {
          crumbs.push({
            label: this.resolveLabel(entry.label),
            url: entry.url,
          });
        }
      } else if (child.data && child.data['breadcrumb']) {
        const label = this.resolveLabel(child.data['breadcrumb'] as string);
        crumbs.push({ label, url: nextUrl });
      }

      return this.buildBreadcrumbs(child, nextUrl, crumbs);
    }

    return crumbs;
  }
}
